/**
 * Applications-list version display helpers.
 *
 * Uploaded binaries are analyzed under a synthetic SemVer-2.0 identity of the form
 * `<manifestVersion>+sha256.<first16>` (built by device-service's buildSyntheticBinaryVersion).
 * eval-service preserves that value as the row's identity `version`/`installedVersion` and exposes a
 * separate clean `displayVersion` (manifest version) for the UI. Store/managed apps have no synthetic
 * suffix and no `manifestVersion`, so `displayVersion` equals their normal version.
 *
 * These helpers let the table show the clean version for uploaded binaries only, leaving store and
 * managed apps (and the substitute-version indicator) exactly as they were.
 */

/** SemVer build-metadata marker that identifies an uploaded-binary synthetic version. */
export const SYNTHETIC_BINARY_VERSION_MARKER = '+sha256.';

/** True when a version string is an uploaded-binary synthetic identity (`<ver>+sha256.<hash>`). */
export const isSyntheticBinaryVersion = (version?: string): boolean =>
  typeof version === 'string' && version.includes(SYNTHETIC_BINARY_VERSION_MARKER);

/**
 * Recovers the clean manifest version from an uploaded binary's synthetic identity
 * (`<manifestVersion>+sha256.<first16hex>`), or undefined when there is no marker.
 *
 * The manifest version is encoded in the analysis subject's `version` — a primary-key component that
 * is always present and never mutated — so this works even when the row's `manifestVersion` was never
 * populated. Mirrors eval-service's `manifestVersionFromSyntheticVersion`, including the `lastIndexOf`
 * so a (pathological) manifest containing the marker still resolves to the trailing suffix.
 */
export const manifestVersionFromSyntheticVersion = (version?: string): string | undefined => {
  if (typeof version !== 'string') return undefined;
  const index = version.lastIndexOf(SYNTHETIC_BINARY_VERSION_MARKER);
  return index > 0 ? version.slice(0, index) : undefined;
};

/**
 * Resolves the version shown on the app detail report (and its PDF).
 *
 * The report is served by `eval/applications/latestAnalysis/:analysisId`, whose payload carries the
 * analysis subject's identity `version` but no `displayVersion` — that field only exists on the
 * Applications search view. Store apps show their version unchanged; uploaded binaries show the clean
 * manifest version, derived from the identity when `manifestVersion` is absent so the internal
 * `+sha256.` suffix can never reach the UI.
 *
 * Identity uses (allow/deny actions, analysis-update event matching) must keep the raw `version`.
 */
export const getReportDisplayVersion = ({
  version,
  analyzedVersion,
  manifestVersion,
}: {
  version?: string;
  analyzedVersion?: string;
  manifestVersion?: string;
}): string | undefined => {
  if (!isSyntheticBinaryVersion(version)) {
    return version;
  }
  return manifestVersion || manifestVersionFromSyntheticVersion(version) || analyzedVersion || version;
};

/**
 * Resolves the version string shown in the Applications list for a row.
 *
 * - Uploaded binaries (synthetic installed version): show the clean `displayVersion`.
 * - Managed apps with installed version "0": fall back to the analyzed version (existing behaviour).
 * - Everything else: show the installed version (existing behaviour).
 */
export const getDisplayedAppVersion = ({
  installedVersion,
  analyzedVersion,
  displayVersion,
}: {
  installedVersion?: string;
  analyzedVersion?: string;
  displayVersion?: string;
}): string | undefined => {
  if (isSyntheticBinaryVersion(installedVersion)) {
    return displayVersion || analyzedVersion || installedVersion;
  }
  return installedVersion === '0' ? analyzedVersion : installedVersion;
};
